import { useCallback, useEffect, useMemo } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import ScrollSmoother from 'gsap/dist/ScrollSmoother';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

import { useCondensedMenu } from 'common/condensedMenu/CondensedMenuProvider';
import { useScrollContext } from 'common/scrolling/scrollerContext';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger, ScrollSmoother, useGSAP);
}
const scrollToItem = (id: string, behavior: ScrollBehavior = 'auto'): Promise<void> => {
  const element = document.getElementById(id);
  if (!element) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            resolve();
            observer.disconnect();
          }
        });
      },
      {
        root: null,
        threshold: 0.1,
      }
    );
    observer.observe(element);

    element.scrollIntoView({
      block: 'start',
      behavior: behavior,
    });
  });
};

const scrollToItemWithGsap = (
  smoother: ScrollSmoother,
  id: string,
  mode: ScrollBehavior = 'auto'
) => {
  if (!smoother) {
    return Promise.resolve();
  }
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  const element = document.getElementById(id);
  if (!element) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const maxScroll = ScrollTrigger.maxScroll(window);
    const targetOffset = smoother.offset(element);
    const scrollTop = Math.min(maxScroll, targetOffset);

    gsap.to(smoother, {
      scrollTop: scrollTop,
      onComplete: () => {
        resolve(true);
      },
      duration: mode === 'auto' ? 0 : 0.5,
      overwrite: 'auto',
    });
  });
};

export const useScrollToItem = () => {
  const { contextSafe } = useGSAP();
  const { isSmoothScrollEnabled, smoothScroller } = useScrollContext();
  const { showMenu } = useCondensedMenu();

  useEffect(() => {
    if (isSmoothScrollEnabled || showMenu) {
      //disable chrome default scroll to remembered position as it interferes with smooth scroll
      if (typeof window !== 'undefined') {
        window.addEventListener('unload', function () {
          window.scrollTo(0, 0);
        });
      }
    }
  }, [isSmoothScrollEnabled, showMenu]);

  const safeScrollToWithGsap = useMemo(() => contextSafe(scrollToItemWithGsap), [contextSafe]);
  const scrollTo = useCallback(
    (id: string, mode?: ScrollBehavior) => {
      if (isSmoothScrollEnabled || showMenu) {
        if (!smoothScroller) {
          //smooths scroll not yet initialized or broken
          return Promise.resolve();
        }
        return safeScrollToWithGsap(smoothScroller, id, mode);
      } else {
        return scrollToItem(id, mode);
      }
    },
    [isSmoothScrollEnabled, showMenu, smoothScroller, safeScrollToWithGsap]
  );

  return { scrollTo };
};
